import { HTMLAttributes } from 'react';

// Types
import { OptionResponse } from '@shared/types';
import { StatisticsType, FormattedStatistics } from '@/types';

// UI
import { Skeleton } from '@shared/ui';

// Utils
import { combineClasses } from '@shared/utils';

// Icons
import { GroupUserIcon } from '@shared/icons/GroupUserIcon';
import { ClockIcon } from '@shared/icons/ClockIcon';
import FileIcon from '@shared/icons/FileIcon';

// Constants
import { EMPTY_FIELD_VALUE } from '@shared/constants';
export const formatDataWithCommonOption = (values: OptionResponse[]) => values.map(({
  name
}) => ({
  value: name,
  label: name
}));

/**
 * Formats statistics data for display in TotalInfo components
 * @param isUser - Whether the current user is a regular user (true) or admin (false)
 * @param data - Raw statistics data from the API
 * @returns Formatted statistics array with appropriate labels, icons, and data for TotalInfo components
 */
export const formatDataStatistics = (isUser: boolean, data?: StatisticsType | null): FormattedStatistics => {
  const userNoneActionUpdate = data?.lastSignIn && data?.lastResumeUpdateAt && data.lastSignIn === data.lastResumeUpdateAt;
  const USER_STATS = [{
    id: '1',
    label: 'Your last login attempt at',
    icon: <ClockIcon className="text-white" />,
    date: data?.lastSignIn || EMPTY_FIELD_VALUE
  }, {
    id: '2',
    label: userNoneActionUpdate ? 'No resume found.\r\nTime to create your first one!' : 'Your resume last updated at',
    icon: <FileIcon className="text-white" />,
    date: userNoneActionUpdate ? EMPTY_FIELD_VALUE : data?.lastResumeUpdateAt || EMPTY_FIELD_VALUE
  }];
  const ADMIN_STATS = [{
    id: '1',
    label: 'Total Users',
    icon: <GroupUserIcon className="text-white" />,
    total: data?.totalUsers?.toString() || '0'
  }, {
    id: '2',
    label: 'Total Resumes',
    icon: <FileIcon className="text-white" />,
    total: data?.totalResumes?.toString() || '0'
  }, {
    id: '3',
    label: 'Last Login',
    icon: <ClockIcon className="text-white" />,
    date: data?.lastSignIn || EMPTY_FIELD_VALUE
  }];
  return isUser ? USER_STATS : ADMIN_STATS;
};

/**
 * Creates a skeleton component with animation control
 * @param isEmpty - If true, disables animation (animate-none)
 * @returns A React component function for creating skeleton elements
 */
export const createSkeletonWithAnimation = (isEmpty: boolean) => ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => <Skeleton className={combineClasses(isEmpty && 'animate-none bg-gray-500/10', className)} {...props} />;